import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {
    NatResidentHeavyLoadsCarryInfo,
    NatResidentHeavyLoadsCarryLocationState,
} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import NatResidentHeavyLoadsCarryForm from './resident/NatResidentHeavyLoadsCarryForm'
import {FileInformation} from '../../../../components/applications/ResidentAttachmentFileArea'
import {NatPutHeavyLoadsCarryRequest, NatPutHeavyLoadsCarryResponse} from '../repository/NatPutHeavyLoadsCarry'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {NatPostFileRequest, NatPostFileResponse} from '../../files/repository/NatPostFile'
import {MESSAGE_TYPE} from '../../../../CodeMaster'
import {NatFileRepo} from '../../files/repository/NatFileRepo'
import {FILE_EXTENSION} from '../../../common/File/NatFileHelper'
import {HKConstants} from '../../../../HKConstants'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applicationRepo: NatApplicationRepo
    fileRepo: NatFileRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentHeavyLoadsCarryLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    heavyWeightGoodsDetail: Nullable<string>
    carryingStartDate: Nullable<string>
    carryingEndDate: Nullable<string>
    applyToAttachment: Nullable<FileInformation[]>
    isLoadCompleted: boolean
}

class NatResidentHeavyLoadsCarryInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            heavyWeightGoodsDetail: null,
            carryingStartDate: null,
            carryingEndDate: null,
            applyToAttachment: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applicationRepo, eventEmitter, condominiumCd} = this.props
        const {applyInfo, applyId} = this.props.location.state

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultHeavyWeightGoodsDetail = applyInfo?.heavyWeightGoodsDetail ?? null
        let defaultCarryingStartDate = applyInfo?.carryingStartDate ?? null
        let defaultCarryingEndDate = applyInfo?.carryingEndDate ?? null
        let defaultApplyToAttachment = applyInfo?.applyToAttachment ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const heavyLoadsCarryDetail = await this.props.asyncCallFunction(
                    applicationRepo.getHeavyLoadsCarryApplyForResident.bind(applicationRepo, condominiumCd, applyId),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (heavyLoadsCarryDetail?.details) {
                    defaultHeavyWeightGoodsDetail = heavyLoadsCarryDetail.details.heavyWeightGoodsDetail
                    defaultCarryingStartDate = heavyLoadsCarryDetail.details.carryingStartDate
                    defaultCarryingEndDate = heavyLoadsCarryDetail.details.carryingEndDate
                    defaultApplyToAttachment = heavyLoadsCarryDetail.details.applyToAttachment.map(attachment => {
                        return {
                            fileName: attachment.fileName,
                            fileData: null,
                            fileIndex: attachment.seq,
                            fileNumber: attachment.fileNumber,
                        }
                    })
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            heavyWeightGoodsDetail: defaultHeavyWeightGoodsDetail,
            carryingStartDate: defaultCarryingStartDate,
            carryingEndDate: defaultCarryingEndDate,
            applyToAttachment: defaultApplyToAttachment,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(
        applyContent: NatResidentHeavyLoadsCarryInfo,
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const copyFileNumbers: number[] = applyContent.applyToAttachment
                .map(attachment => attachment.fileNumber)
                .filter((fileNumber): fileNumber is Exclude<typeof fileNumber, undefined> => fileNumber !== undefined)
                .filter((fileNumber): fileNumber is Exclude<typeof fileNumber, null> => fileNumber !== null)

            const applyRequest: NatPutHeavyLoadsCarryRequest = {
                applicantFamilyName: applyContent.applicantFamilyName,
                applicantFirstName: applyContent.applicantFirstName,
                heavyWeightGoodsDetail: applyContent.heavyWeightGoodsDetail,
                carryingStartDate: applyContent.carryingStartDate,
                carryingEndDate: applyContent.carryingEndDate,
                reApplyCopyFileNumbers: copyFileNumbers,
                confirmed: false,
            }

            const extensions = [
                ...FILE_EXTENSION.JPEG,
                ...FILE_EXTENSION.PNG,
                ...FILE_EXTENSION.PDF,
            ]
            const fileRequests: NatPostFileRequest[] = applyContent.applyToAttachment
                .filter(attachmentFile => attachmentFile.fileNumber === null)
                .map((attachmentFile) => {
                    return ({
                        fileName: attachmentFile.fileName,
                        fileData: attachmentFile.fileData ?? null,
                        fileIndex: attachmentFile.fileIndex,
                        fileNumber: attachmentFile.fileNumber ?? null,
                        maxSizeMB: HKConstants.CMN.OPC.UPLOAD_FILE_LIMIT_MEGABYTES,
                        extension: extensions,
                    })
                })

            const applySuccess = await this.applyHeavyLoadsCarry(applyRequest)
            if (!applySuccess) {
                return
            }

            const fileSuccess = await this.fileUpload(fileRequests)
            if (!fileSuccess) {
                return
            }

            const locationState: NatResidentHeavyLoadsCarryLocationState = {
                applyInfo: {
                    applicantFamilyName: applyRequest.applicantFamilyName,
                    applicantFirstName: applyRequest.applicantFirstName,
                    heavyWeightGoodsDetail: applyRequest.heavyWeightGoodsDetail,
                    carryingStartDate: applyRequest.carryingStartDate,
                    carryingEndDate: applyRequest.carryingEndDate,
                    applyToAttachment: applyContent.applyToAttachment,
                },
                applyId: this.props.location.state.applyId,
            }

            this.props.navigate(
                `/resident/condominiums/${this.props.condominiumCd}/applications/heavyLoadsCarries/confirm`,
                {state: JSON.parse(JSON.stringify(locationState))},
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async applyHeavyLoadsCarry(request: NatPutHeavyLoadsCarryRequest): Promise<boolean> {
        const {applicationRepo, condominiumCd} = this.props

        const response: Nullable<NatPutHeavyLoadsCarryResponse> = await this.props.asyncCallFunction(
            applicationRepo.applyHeavyLoadsCarry.bind(
                applicationRepo, condominiumCd, request),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
            },
            {
                defaultErrorTitle: Messages.EOPCR0014.toString(),
                defaultErrorTitlePriority: true,
                replacementDetailErrorMessages: [{
                    targetMessageId: Messages.EOPCA0004,
                    replacementMessageId: Messages.EOPCR0015,
                }],
            },
        )
        return !!(response && response.requestError === null)
    }

    async fileUpload(requests: NatPostFileRequest[]): Promise<boolean> {
        const {fileRepo} = this.props

        let reportIndex: number = 1
        const fileCount: number = requests.length
        const responses: Nullable<NatPostFileResponse>[] = []

        for (const request of requests) {
            this.props.eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${reportIndex}/${fileCount}`)

            const fileResponse = await this.props.asyncCallFunction(
                fileRepo.fileUpload.bind(
                    fileRepo, request),
                async (httpErrorOrNetworkError) => {
                    const requestItemErrors = httpErrorOrNetworkError.responseBody.requestItemErrors
                    requestItemErrors.forEach((item) => {
                        this.props.eventEmitter.emit(item.resultCode, item)
                    })
                },
            )
            reportIndex = reportIndex + 1
            responses.push(fileResponse)
        }

        if (responses.filter(file => file && file.requestItemErrors === null).length === fileCount) {
            return true
        } else {
            await this.props.eventEmitter.showHeadingAreaMessage({
                title: Messages.EOPCR0014.toString(),
                messageLevel: MESSAGE_TYPE.INFORMATION,
                detail: Messages.EOPCA0005.toString(),
            })
            return false
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.location.state.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/heavyLoadsCarries/${this.props.location.state.applyId}/copy`
                        :
                        `/resident/condominiums/${this.props.condominiumCd}/applications/heavyLoadsCarries/agreement`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    重量物搬入等届
                </PageTitle>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentHeavyLoadsCarryForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentHeavyLoadsCarryInputPage)
